<script setup>
import { JChat } from "media-flow-ui"
import { watch } from "vue"
import { useSignalrBuilder } from "~/stores/signalr-builder"
import { useChatBuilder } from "~/stores/chat-builder"
import { useAuthBuilder } from "~/stores/auth-builder"
import { SIGNALR_CONNECTION } from "~/constants/signalr/signalrConnections"
import { storeToRefs } from "pinia"
import { DOM_ELEMENT } from "@/constants/elements"
import { CHAT_STATUS_TYPE } from "@/constants/chat"
import { $$t } from "~~/utils/i19m"
import SSEService from "~/services/sseService"
import { seekedTime } from "@/helpers/chat/index"
import { useUrlBuilder } from "~/stores/url-builder"

import { usePageBuilder } from "~/stores/page-builder"
import ChatMessage from "~/models/chat/ChatMessage"

const pageBuilderStore = usePageBuilder()

const signalrBuilderStore = useSignalrBuilder()
const chatBuilderStore = useChatBuilder()
const authBuilderStore = useAuthBuilder()
const urlBuilder = useUrlBuilder()

const { getLayoutMediaData } = storeToRefs(pageBuilderStore)
const { newestChatMessage, chatConfig, chatIdToRemove } =
  storeToRefs(signalrBuilderStore)
const { getProjectId } = storeToRefs(urlBuilder)
const { getCurrentUserAsAuthor } = storeToRefs(authBuilderStore)

const props = defineProps({
  data: {
    type: Object,
    required: false,
    default: false,
  },
})

const chats = ref([])
const participants = ref([])
const mentionUsers = ref([])
const showLoading = ref(true)
const showChatParticipantsLoader = ref(false)
// const message = ref(null);
const playerHeight = ref(null)
const chatId = ref(null)
const videoStartDate = ref(null)
const chatError = ref(false)
const isLoading = ref(true)
const isChatReplay = ref(false)
const participantsPage = ref(0)
const participantsEnabled = ref(false)
const mentionsLoader = ref(true)
const mentionsPage = ref(0)
const mentionSearchTerm = ref(null)
const config = ref({
  active: true,
})
const chatRef = ref(null)
const sseService = ref(null)
//check and get Chat data

const postMessage = async (data) => {
  const res = await chatBuilderStore.actionPostChatMessage(chatId.value, data)

  //handle errors if not allowed to chat
  if (res.error && chatRef.value) {
    chatRef.value.showInfoMessage(res?.message)
  }
}

const filterMentions = (data) => {
  if (!data) return
  mentionSearchTerm.value = data
  mentionsPage.value = 0
  getMentions(false, true)
}

const checkAndCorrectLengthOfArray = (data) => {
  if (chats.value.length > 45) {
    chats.value.splice(0, chats.value.length - 45)
  }
}

const pushNewMessagesToChat = (data) => {
  chats.value.push(data)
  checkAndCorrectLengthOfArray()
}

const removeChatMessageById = (data) => {
  if (!data) return
  const indexFound = chats.value.findIndex((c) => c.chatMessageId === data)
  chats.value.splice(indexFound, 1)
}

watch(
  () => newestChatMessage.value,
  (first, second) => {
    pushNewMessagesToChat(newestChatMessage.value)
  }
)

watch(
  () => chatIdToRemove.value,
  (first, second) => {
    removeChatMessageById(chatIdToRemove.value)
  }
)

watch(
  () => seekedTime.value,
  (val) => {
    if (!chatId.value) return
    if (chatConfig.value.active) return
    startSSEService(chatId.value, videoStartDate.value, val)
  }
)

watch(
  () => chatConfig.value.inReplayMode,
  (val) => {
    if (!val) {
      if (sseService.value) sseService.value.close()
      chats.value.length = 0
      isChatReplay.value = false
      loadOldChatMessages()
    } else {
      if (sseService.value) sseService.value.close()
      isChatReplay.value = true
      startSSEService(chatId.value, videoStartDate.value, seekedTime.value)
    }
  }
)

const loadMoreChatMesagges = async (lastChatId) => {
  if (!lastChatId) return

  try {
    const response = await chatBuilderStore.actionGetChatMessages(
      chatId.value,
      lastChatId
    )

    if (response && response.length > 0) {
      showLoading.value = true
    } else {
      showLoading.value = false
    }

    if (response) {
      chats.value.unshift(...response)
    }
  } catch (e) {
    console.error(e)
  }
}

const loadMoreChatParticipants = async (onload = true) => {
  if (onload) participantsPage.value++
  try {
    const response = await chatBuilderStore.actionGetChatParticipants(
      chatId.value,
      participantsPage.value
    )
    if (response && response.items.length >= 10) {
      showChatParticipantsLoader.value = false
    } else {
      showChatParticipantsLoader.value = true
    }

    if (response) {
      participants.value.push(...response.items)
    }
  } catch (e) {
    console.error(e)
  }
}

const getMentions = async (onload = false, onsearch = false) => {
  if (onload && !onsearch) {
    mentionsPage.value++
  }
  if (!onload && !onsearch) {
    mentionsPage.value = 0
    mentionSearchTerm.value = ""
  }

  try {
    const response = await chatBuilderStore.actionGetMentionUsers(
      chatId.value,
      {
        page: mentionsPage.value,
        search: mentionSearchTerm.value,
      }
    )

    if (response) {
      onload
        ? mentionUsers.value.push(...response.mentions.items)
        : (mentionUsers.value = response.mentions.items)

      mentionsLoader.value = response.mentions.items.length < 10 ? false : true
    }
  } catch (e) {
    console.error(e)
  }
}

const loadMoreMentions = async () => {
  getMentions(true, false)
}

const loadOldChatMessages = async () => {
  try {
    const response = await chatBuilderStore.actionGetChatMessages(chatId.value)

    if (response) {
      chats.value.push(...response)
    } else {
      chatError.value = true
    }
  } catch (e) {
    chatError.value = true
    chatId.value = null
    console.error(e)
  } finally {
    isLoading.value = false
  }
}

function addMessage(data) {
  const jsonData = JSON.parse(data)
  const message = new ChatMessage()
  message.formData(jsonData)
  chats.value.push(message)
}

function startSSEService(id, videoStartDate, seekedTime) {
  if (sseService.value) sseService.value.close()
  chats.value.length = 0

  sseService.value = new SSEService(
    getProjectId.value,
    id,
    videoStartDate,
    seekedTime
  )
  sseService.value.onEvent("chatmessage", (data) => {
    addMessage(data)
  })

  sseService.value.onError((error) => console.error("SSE Error:", error))
}

onMounted(async () => {
  chatId.value = getLayoutMediaData.value.chatId // || "OrJvM7KMG5Z";

  if (!chatId.value) return
  if (getLayoutMediaData.value.liveStartDate) {
    videoStartDate.value = getLayoutMediaData.value.liveStartDate.split("+")[0]
  } else {
    videoStartDate.value = getLayoutMediaData.value.startDate.split("+")[0]
  }

  let response = null
  try {
    response = await chatBuilderStore.actionGetChatConfig(chatId.value)
    if (CHAT_STATUS_TYPE.ACTIVE === response.chatStatusTypeId) {
      chatConfig.value.active = true
    } else if (CHAT_STATUS_TYPE.INACTIVE === response.chatStatusTypeId) {
      chatConfig.value.active = false
      chatConfig.value.message = $$t("component_chat_chat_ended") //"Chat has ended";
    } else if (CHAT_STATUS_TYPE.PAUSED === response.chatStatusTypeId) {
      chatConfig.value.active = false
      chatConfig.value.message = $$t("component_chat_paused") // "Chat has been paused";
    } else if (CHAT_STATUS_TYPE.SCHEDULED === response.chatStatusTypeId) {
      chatConfig.value.active = false
      chatConfig.value.message = $$t("component_chat_scheduled") //"Chat scheduled. Will start soon.";
    } else if (CHAT_STATUS_TYPE.REPLAY === response.chatStatusTypeId) {
      isChatReplay.value = true
      chatConfig.value.active = false
      startSSEService(chatId.value, videoStartDate.value, seekedTime.value)
      chatConfig.value.message = $$t("component_chat_replay") //"Chat replay";
    }
    await loadMoreChatParticipants(false)
    if (!isChatReplay.value) {
      await loadOldChatMessages()
    } else {
      isLoading.value = false
    }

    participantsEnabled.value = response.participantsEnabled
  } catch (error) {
    chatId.value = null
    console.error(error)
  }

  if (chatError.value) return

  signalrBuilderStore.setToJoinQueue(SIGNALR_CONNECTION.JOIN_CHAT, chatId.value)

  const playerHeightEl = document.getElementById(DOM_ELEMENT.VP_PLAYER_HOLDER)
  if (playerHeightEl) {
    if (playerHeightEl.clientHeight < 400) {
      playerHeight.value = `400px`
    } else {
      playerHeight.value = `${playerHeightEl.clientHeight}px`
    }
  }

  if (!getCurrentUserAsAuthor.value.email) {
    chatConfig.value.active = false
    chatConfig.value.message = $$t("component_chat_requires_login") //`Login if you want to interact with chat.`;
  }
})

const login = async () => {
  try {
    await authBuilderStore.callLogin()
  } catch (error) {
    console.error("Login click", error)
  }
}

onUnmounted(() => {
  signalrBuilderStore.leaveGroup(chatId.value)
  sseService.value?.close()
  seekedTime.value = 0
})
</script>

<template>
  <div class="chat-wrapper" v-if="chatId">
    <!-- <div class="text-red-200">
      
      {{getLayoutMediaData.chatId}}
    </div> -->

    <div v-if="isLoading">Loading Chat...</div>

    <div
      v-else-if="chatError"
      class="chat-issues"
      :class="`chat-error-${chatError}`"
    ></div>
    <JChat
      v-else
      :id="data.id"
      ref="chatRef"
      :hideChatText="$$t('component_chat_button_hide_chat')"
      :showChatText="$$t('component_chat_button_show_chat')"
      :participantsChatText="$$t('component_chat_button_participants')"
      :timestampsChatText="$$t('component_chat_button_timestamps')"
      :inputText="$$t('component_chat_input_placeholder')"
      :buttonText="$$t('component_chat_button_send')"
      :title="data.props.title"
      :config="chatConfig"
      :customHeight="playerHeight"
      :chatMessages="chats"
      :style="data.style"
      :postMessageCallback="postMessage"
      :loadMoreOldMesaggesCallback="loadMoreChatMesagges"
      :loadMoreOldParticipantsCallback="loadMoreChatParticipants"
      :profile="getCurrentUserAsAuthor"
      :mentionUsers="mentionUsers"
      :participantsList="participants"
      :showParticipantsLoader="showChatParticipantsLoader"
      :loadMentionUsers="getMentions"
      :send-button-type="data.style.footer.type"
      :participants-enabled="participantsEnabled"
      :laodMoreMentionsCallback="loadMoreMentions"
      :hideMentionUserLoader="mentionsLoader"
      :mentionsSearchCallback="filterMentions"
      :chat-in-replay-mode="isChatReplay"
    >
      <template #loadMoreSpinner>
        <div v-if="showLoading" class="overflow-hidden">
          <div
            class="border-gray-300 min-h-[24px] min-w-[24px] animate-spin rounded-full border-4 border-t-blue-600 my-1"
          />
        </div>
      </template>
      <template #chatFooterInfo>
        <span class="flex gap-2 items-center footerSlot"
          ><span
            ><svg
              width="12"
              height="13"
              viewBox="0 0 12 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M6.00008 12.3327C9.22174 12.3327 11.8334 9.72101 11.8334 6.49935C11.8334 3.27769 9.22174 0.666016 6.00008 0.666016C2.77842 0.666016 0.166748 3.27769 0.166748 6.49935C0.166748 9.72101 2.77842 12.3327 6.00008 12.3327ZM6.58341 2.99935C6.58341 3.32151 6.32225 3.58268 6.00008 3.58268C5.67792 3.58268 5.41675 3.32151 5.41675 2.99935C5.41675 2.67718 5.67792 2.41602 6.00008 2.41602C6.32225 2.41602 6.58341 2.67718 6.58341 2.99935ZM6.00008 4.74935C6.32225 4.74935 6.58341 5.01052 6.58341 5.33268V8.24935H7.16675C7.48891 8.24935 7.75008 8.51052 7.75008 8.83268C7.75008 9.15485 7.48891 9.41602 7.16675 9.41602H4.83341C4.51125 9.41602 4.25008 9.15485 4.25008 8.83268C4.25008 8.51052 4.51125 8.24935 4.83341 8.24935H5.41675V5.91602H4.83341C4.51125 5.91602 4.25008 5.65485 4.25008 5.33268C4.25008 5.01052 4.51125 4.74935 4.83341 4.74935H6.00008Z"
                fill="#FFFFFF"
              />
            </svg> </span
          ><span class="text-[#FFFFFF]">{{ chatConfig.message }}</span>
          <span
            v-if="!getCurrentUserAsAuthor?.email"
            class="text-[#206ED5] cursor-pointer"
            @click="login"
            >{{ $$t("component_chat_callback_login") }}</span
          >
        </span>
      </template>
    </JChat>
  </div>
</template>

<style lang="scss" scoped>
.footerSlot {
  background: #000;
  padding: 5px;
  width: 100%;
  margin-top: 20px;
  border-radius: 5px;
}
</style>
